import { useEffect, useState } from 'react'
import { Alert, DatePicker, Form, Input, Modal, Select, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { tenantToday } from '@/shared/lib/dayRange'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { REASON_MAX } from './hrApi'
import { leaveDays, requestLeave, type LeaveType } from './leaveApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

const DATE_FORMAT = 'YYYY-MM-DD'

interface Props {
  open: boolean
  personId: string
  /** ⚠️ FAQAT faol turlar — o'chirilgan turga so'rov yozilmaydi */
  types: readonly LeaveType[]
  onClose: () => void
}

interface FormValues {
  leaveTypeId: string
  range: [Dayjs, Dayjs]
  reason: string
}

/**
 * Ta'til so'rovi.
 *
 * <h2>⚠️ SANALAR — KALENDAR KUNI, IKKALASI HAM ICHIGA KIRADI</h2>
 * Server `days = endsOn - startsOn + 1` deb sanaydi va bu KALENDAR
 * kuni: bayram kalendari yo'q (§20.8), dam olish kunini taxmin qilish
 * esa Afg'onistonda xato bo'lardi (u yerda payshanba-juma). Oynada
 * kunlar soni SHU QOIDA bo'yicha oldindan ko'rsatiladi — operator
 * «10 kun so'radim, 12 chiqdi» holatiga tushmasin.
 *
 * <h2>⚠️ «BUGUN» TENANT ZONASIDA</h2>
 * Boshlang'ich qiymat `tenantToday()` dan (`dayRange.ts`), `dayjs()`
 * dan EMAS: Toshkentdan boshqarilayotgan Kobul tenantida kuniga yarim
 * soat davomida ular ikki xil sanada turadi.
 *
 * <h2>⚠️ BALANS YETMASA SERVER 409 BERADI</h2>
 * Panel balansni oldindan tekshirmaydi — u ikki manba yaratardi va
 * ular ajralib ketardi. Server javobi oynaning ICHIDA ko'rinadi
 * (`guards.mjs` 1-bo'limi), ya'ni sabab o'qiladi.
 */
export function LeaveRequestModal({ open, personId, types, onClose }: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const [days, setDays] = useState(0)

  useEffect(() => {
    if (!open) return
    const today = tenantToday()
    form.setFieldsValue({ leaveTypeId: undefined, range: [today, today], reason: '' })
    setDays(1)
  }, [open, form])

  const request = useMutation({
    mutationFn: (values: FormValues) =>
      requestLeave(personId, {
        leaveTypeId: values.leaveTypeId,
        startsOn: values.range[0].format(DATE_FORMAT),
        endsOn: values.range[1].format(DATE_FORMAT),
        reason: values.reason.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      form.resetFields()
      onClose()
    },
  })

  const close = () => {
    request.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('hr.leave.requestTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => request.mutate(values))}
      okButtonProps={{ disabled: types.length === 0 }}
      confirmLoading={request.isPending}
      okText={t('hr.leave.requestAction')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      <div style={{ marginBottom: 16 }}>
        <ExpandableHint text={t('hr.leave.requestNote')} fontSize={13} />
      </div>

      {request.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(request.error, t)}
        />
      ) : null}

      {types.length === 0 ? (
        <Alert type="warning" showIcon message={t('hr.leave.noTypes')} />
      ) : (
        <Form<FormValues>
          form={form}
          layout="vertical"
          preserve={false}
          disabled={request.isPending}
          onValuesChange={(_changed, values) => {
            const range = values.range
            setDays(
              range?.[0] && range[1]
                ? leaveDays(range[0].format(DATE_FORMAT), range[1].format(DATE_FORMAT))
                : 0,
            )
          }}
        >
          <Form.Item
            name="leaveTypeId"
            label={t('hr.leave.type')}
            rules={[{ required: true, message: t('hr.leave.validation.typeRequired') }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('hr.leave.typePlaceholder')}
              options={types.map((type) => ({
                value: type.id,
                label: type.paid ? `${type.name} · ${t('hr.leave.paid')}` : type.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="range"
            label={t('hr.leave.range')}
            extra={t('hr.leave.rangeHint')}
            rules={[{ required: true, message: t('hr.leave.validation.rangeRequired') }]}
          >
            <DatePicker.RangePicker format={DATE_FORMAT} style={{ width: '100%' }} />
          </Form.Item>

          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: -8 }}>
            {t('hr.leave.daysComputed', { count: days })}
          </Typography.Paragraph>

          <Form.Item
            name="reason"
            label={t('hr.leave.reason')}
            extra={t('hr.leave.reasonHint')}
            rules={[
              { required: true, message: t('hr.salary.validation.reasonRequired') },
              { max: REASON_MAX, message: t('hr.assignment.validation.reasonLength') },
            ]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
